import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiFehler } from "../api/client";
import {
  useAnlage,
  useAnlageAblehnen,
  useAnlageBestaetigen,
  useAnlageSpeichern,
  useExtraktionWiederholen,
  useInBearbeitungSetzen,
} from "../api/hooks";
import type { Anlage, Dokument } from "../api/typen";
import { AnlagenFormular } from "../komponenten/AnlagenFormular";
import { Hinweis, Karte, Knopf, Modal, Spinner, StatusBadge } from "../komponenten/Basis";
import { DokumentVorschau } from "../komponenten/DokumentVorschau";
import { FeldListe, VollmachtChecks } from "../komponenten/Extraktion";
import { KonfidenzLegende } from "../komponenten/Konfidenz";
import { KundenLinks } from "../komponenten/KundenLinks";
import { KundeLoeschenKnopf } from "../komponenten/Loeschen";
import { NachreichungenBanner, ZuruecknehmenKnopf } from "../komponenten/Nachreichungen";
import { StatusIndividuell } from "../komponenten/StatusIndividuell";
import { StatusLinkBereich } from "../komponenten/StatusLinkBereich";
import { useToast } from "../kontext/ToastKontext";
import { VERLAUF_PRAEFIX, useUndoRedo, verlaufLoeschen } from "../hooks/verlauf";
import {
  ALLE_FELDER,
  FELD_LABEL,
  formAusAnlage,
  geaenderteFelder,
  putBody,
  validiereFormular,
  type FeldName,
  type FormWerte,
} from "../lib/felder";
import { EXTRAKTION_TEXT, TYP_ICON, TYP_TEXT, zeitpunkt } from "../lib/format";
import { GEPRUEFT } from "../lib/konfidenz";

export function PruefSeite() {
  const { id } = useParams();
  const anlageId = Number(id);
  const abfrage = useAnlage(anlageId);

  if (!Number.isInteger(anlageId) || anlageId <= 0) return <Hinweis art="fehler">Ungültige Anlagen-ID.</Hinweis>;
  if (abfrage.isLoading) return <div className="flex justify-center py-24"><Spinner label="Anlage wird geladen…" /></div>;
  if (abfrage.error || !abfrage.data) {
    const nichtGefunden = abfrage.error instanceof ApiFehler && abfrage.error.status === 404;
    return (
      <div className="mx-auto max-w-lg space-y-4 py-12">
        <Hinweis art="fehler">{nichtGefunden ? `Anlage #${anlageId} gibt es nicht.` : (abfrage.error?.message ?? "Anlage konnte nicht geladen werden.")}</Hinweis>
        <div className="flex gap-2">
          {!nichtGefunden && <Knopf onClick={() => void abfrage.refetch()}>Erneut laden</Knopf>}
          <Link to="/anlagen" className="rounded-lg px-4 py-2 text-sm font-semibold underline">Zur Übersicht</Link>
        </div>
      </div>
    );
  }
  return <PruefAnsicht anlage={abfrage.data} neuLaden={() => void abfrage.refetch()} />;
}

function PruefAnsicht({ anlage, neuLaden }: { anlage: Anlage; neuLaden: () => void }) {
  const toast = useToast();
  const speichern = useAnlageSpeichern(anlage.id);
  const bestaetigen = useAnlageBestaetigen(anlage.id);
  const ablehnen = useAnlageAblehnen(anlage.id);
  const wiederholen = useExtraktionWiederholen();
  const liveSetzen = useInBearbeitungSetzen(anlage.id);

  const nurLesen = anlage.status === "final" || anlage.status === "angemeldet";
  const verlaufSchluessel = `${VERLAUF_PRAEFIX}anlage.${anlage.id}`;
  const basis = useMemo(() => formAusAnlage(anlage), [anlage]);
  const verlauf = useUndoRedo<FormWerte>(verlaufSchluessel, basis, anlage.version, !nurLesen);
  const [serverFehler, setServerFehler] = useState<Partial<Record<FeldName, string>>>({});
  const [bestaetigenOffen, setBestaetigenOffen] = useState(false);
  const [ablehnenOffen, setAblehnenOffen] = useState(false);
  const [grund, setGrund] = useState("");
  const [neuExtrahieren, setNeuExtrahieren] = useState(true);

  const dokumente = anlage.dokumente ?? [];
  const [dokumentId, setDokumentId] = useState<number | null>(() => (dokumente.find((d) => d.typ !== "foto") ?? dokumente[0])?.id ?? null);
  const dokument: Dokument | undefined = dokumente.find((d) => d.id === dokumentId) ?? dokumente[0];

  const werte = verlauf.werte;
  const geaendert = useMemo(() => new Set(geaenderteFelder(basis, werte)), [basis, werte]);
  const [markiert, setMarkiert] = useState<Set<FeldName>>(new Set());
  // Alles, was beim Speichern als „vom Menschen geprüft“ gesendet wird.
  const zuSenden = useMemo(() => new Set<FeldName>([...geaendert, ...markiert]), [geaendert, markiert]);
  const clientFehler = useMemo(() => validiereFormular(werte), [werte]);
  const fehler = { ...serverFehler, ...clientFehler };
  const beschaeftigt = speichern.isPending || bestaetigen.isPending || ablehnen.isPending;

  // Neue Server-Version (eigenes Speichern, Neu-Extraktion, Kollege) → Formular übernehmen.
  // Der Verlauf bleibt erhalten, so lassen sich eigene Eingaben per Undo zurückholen.
  const { neueBasis, version: verlaufVersion } = verlauf;
  useEffect(() => {
    if (verlaufVersion === anlage.version) return;
    neueBasis(basis, anlage.version, true);
    setServerFehler({});
    setMarkiert(new Set());
  }, [anlage.version, basis, neueBasis, verlaufVersion]);

  function aendern(name: FeldName, wert: string) {
    setServerFehler((alt) => {
      const { [name]: _entfernt, ...rest } = alt;
      return rest;
    });
    verlauf.setzen({ ...werte, [name]: wert }, name);
  }

  async function speichernAusfuehren(): Promise<Anlage | null> {
    if (Object.keys(clientFehler).length) {
      toast.fehler(new Error(Object.entries(clientFehler).map(([f, m]) => `${FELD_LABEL[f]}: ${m}`).join(" · ")), "Bitte Eingaben korrigieren");
      return null;
    }
    const felder = [...zuSenden];
    try {
      const neu = await speichern.mutateAsync(putBody(werte, felder, anlage.version));
      return neu;
    } catch (e) {
      if (e instanceof ApiFehler && e.code === "version_conflict") {
        toast.fehler(e, "Parallele Änderung", { label: "Neu laden", ausfuehren: neuLaden });
      } else if (e instanceof ApiFehler && e.status === 422) {
        setServerFehler(e.feldFehler as Partial<Record<FeldName, string>>);
        toast.fehler(e, "Speichern abgelehnt");
      } else {
        toast.fehler(e, "Speichern fehlgeschlagen");
      }
      return null;
    }
  }

  async function beiSpeichern() {
    const anzahl = zuSenden.size;
    const neu = await speichernAusfuehren();
    if (neu) toast.erfolg("Gespeichert", anzahl ? `${anzahl} Feld(er) übernommen – Status: Validiert` : "Anlage als geprüft (validiert) markiert");
  }

  function markieren(name: FeldName) {
    setMarkiert((alt) => {
      const neu = new Set(alt);
      if (neu.has(name)) neu.delete(name);
      else neu.add(name);
      return neu;
    });
  }

  async function beiBestaetigen() {
    if (zuSenden.size > 0 || anlage.status === "draft") {
      if (!(await speichernAusfuehren())) return;
    }
    try {
      const ergebnis = await bestaetigen.mutateAsync();
      verlaufLoeschen(verlaufSchluessel);
      setBestaetigenOffen(false);
      toast.erfolg("Anlage bestätigt", `Status final · ${zeitpunkt(ergebnis.bestaetigt_am)}`);
    } catch (e) {
      if (e instanceof ApiFehler && e.code === "nicht_bestaetigbar") {
        const details = e.details as { fehlende_felder?: string[]; ungueltige_felder?: Record<string, string> };
        const teile = [
          ...(details.fehlende_felder ?? []).map((f) => `${FELD_LABEL[f] ?? f} fehlt`),
          ...Object.entries(details.ungueltige_felder ?? {}).map(([f, m]) => `${FELD_LABEL[f] ?? f}: ${m}`),
        ];
        toast.fehler(new Error(teile.join(" · ")), "Noch nicht bestätigbar");
      } else {
        toast.fehler(e, "Bestätigen fehlgeschlagen");
      }
    }
  }

  function beiAblehnen() {
    if (grund.trim().length < 3) return;
    ablehnen.mutate(
      { grund: grund.trim(), neu_extrahieren: neuExtrahieren },
      {
        onSuccess: () => {
          verlaufLoeschen(verlaufSchluessel);
          setAblehnenOffen(false);
          setGrund("");
          toast.info("Anlage zurückgesetzt", neuExtrahieren ? "Dokumente wurden neu extrahiert – bitte erneut prüfen." : "Status: Entwurf");
        },
        onError: (e) => toast.fehler(e, "Ablehnen fehlgeschlagen"),
      },
    );
  }

  function dokumentNeuExtrahieren(d: Dokument) {
    wiederholen.mutate(d.id, {
      onSuccess: (neu) =>
        neu.extraktion_status === "erfolgreich"
          ? toast.erfolg("Neu extrahiert", d.original_dateiname)
          : toast.fehler(new Error(neu.fehler ?? "Extraktion fehlgeschlagen"), "Extraktion fehlgeschlagen"),
      onError: (e) => toast.fehler(e, "Extraktion konnte nicht gestartet werden"),
    });
  }

  // Tastenkürzel: Strg+Z / Strg+Y (bzw. Strg+Umschalt+Z) / Strg+S
  const { rueckgaengig, wiederholen: redo } = verlauf;
  useEffect(() => {
    if (nurLesen) return;
    function taste(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        rueckgaengig();
      } else if (k === "y" || (k === "z" && e.shiftKey)) {
        e.preventDefault();
        redo();
      } else if (k === "s") {
        e.preventDefault();
        document.getElementById("knopf-speichern")?.click();
      }
    }
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [nurLesen, rueckgaengig, redo]);

  const ungeprueftUnsicher = ALLE_FELDER.filter((f) => {
    const k = anlage.konfidenz[f.name];
    return !zuSenden.has(f.name) && werte[f.name].trim() !== "" && k !== undefined && k < 0.8 && k < GEPRUEFT;
  });
  const fehlendePflicht = ALLE_FELDER.filter((f) => f.pflicht && werte[f.name].trim() === "");
  // Die neueste Vollmacht zählt (wie beim Übernehmen der Werte), nicht die erste.
  const vollmachtDokument = [...dokumente].reverse().find((d) => d.typ === "vollmacht" && d.pruefungen);

  return (
    <div className="space-y-5 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/anlagen" className="text-sm text-neutral-500 hover:text-neutral-900">← Anlagen-Übersicht</Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 text-2xl font-semibold tracking-tight">
            Anlage #{anlage.id} · {anlage.kunde.name}
            <StatusBadge status={anlage.status} />
          </h1>
          <p className="mt-1 text-xs text-neutral-500">
            Version {anlage.version} · zuletzt geändert {zeitpunkt(anlage.updated_at)}
            {anlage.bestaetigt_am && ` · bestätigt ${zeitpunkt(anlage.bestaetigt_am)}`}
          </p>
          <button
            type="button"
            aria-pressed={anlage.in_bearbeitung}
            disabled={liveSetzen.isPending}
            onClick={() => liveSetzen.mutate(!anlage.in_bearbeitung)}
            title="Steuert nur den Status, den Installateur und Endkunde sehen – „In Bearbeitung“ statt „Eingegangen“."
            className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition ${
              anlage.in_bearbeitung
                ? "bg-ampel-gruenHell text-ampel-gruen ring-ampel-gruen/30"
                : "bg-neutral-100 text-neutral-600 ring-neutral-300 hover:bg-neutral-200"
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${anlage.in_bearbeitung ? "bg-ampel-gruen" : "bg-neutral-400"}`} aria-hidden />
            {anlage.in_bearbeitung ? "Ich sitze gerade live dran" : "Als „In Bearbeitung“ markieren"}
          </button>
          <StatusIndividuell anlage={anlage} />
        </div>
        <div className="flex flex-col items-end gap-2">
          <Link
            to={`/anlagen/${anlage.id}/mastr`}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              anlage.status === "final" ? "bg-marke text-white hover:bg-marke-hover" : "border border-neutral-300 bg-white hover:bg-neutral-50"
            }`}
          >
            MaStR & Netzbetreiber →
          </Link>
          <KonfidenzLegende />
          <StatusLinkBereich anlage={anlage} />
          <KundeLoeschenKnopf kunde={anlage.kunde} />
        </div>
      </div>

      <NachreichungenBanner anlage={anlage} beiAnsehen={setDokumentId} />
      <KundenLinks anlageId={anlage.id} beiAnsehen={setDokumentId} />
      {nurLesen && <Hinweis art="erfolg">Diese Anlage ist bestätigt und schreibgeschützt.</Hinweis>}
      {!nurLesen && verlauf.wiederhergestellt && geaendert.size > 0 && (
        <Hinweis art="info">Ungespeicherte Korrekturen aus deiner letzten Sitzung wurden wiederhergestellt ({geaendert.size} Feld(er)).</Hinweis>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        {/* ---------------------------------------------------------------- Original */}
        <Karte className="p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {dokumente.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDokumentId(d.id)}
                className={`rounded-lg border px-3 py-1.5 text-left text-xs font-semibold transition ${
                  d.id === dokument?.id ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white hover:bg-neutral-50"
                }`}
                title={d.original_dateiname}
              >
                {TYP_ICON[d.typ]} {TYP_TEXT[d.typ]}
                <span className={`ml-1.5 font-normal ${d.id === dokument?.id ? "text-neutral-300" : d.extraktion_status === "fehlgeschlagen" ? "text-ampel-rot" : "text-neutral-500"}`}>
                  {EXTRAKTION_TEXT[d.extraktion_status]}
                </span>
                {d.nachreichung_status === "offen" && <span className="ml-1.5 rounded bg-ampel-gelb px-1.5 py-0.5 text-[10px] font-bold text-white">neu</span>}
              </button>
            ))}
            {!nurLesen && (
              <Link to={`/?kunde=${anlage.kunde.id}&anlage=${anlage.id}`} className="ml-auto rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50">
                + Dokument hinzufügen
              </Link>
            )}
          </div>

          {dokument ? (
            <div className="space-y-4">
              <DokumentVorschau dokument={dokument} />
              {dokument.extraktion_status === "fehlgeschlagen" && (
                <Hinweis art="fehler">
                  <p className="font-semibold">{dokument.fehler}</p>
                  {dokument.hinweis && <p className="mt-1">{dokument.hinweis}</p>}
                </Hinweis>
              )}
              {dokument.typ !== "foto" && !nurLesen && (
                <Knopf onClick={() => dokumentNeuExtrahieren(dokument)} laedt={wiederholen.isPending}>
                  ↻ Dieses Dokument neu extrahieren
                </Knopf>
              )}
              {(dokument.quelle === "portal" || dokument.nachgereicht_am) && (
                <p className="text-xs text-neutral-500">
                  Vom Installateur hochgeladen{dokument.nachgereicht_am ? `, nachgereicht ${zeitpunkt(dokument.nachgereicht_am)}` : ""}
                  {dokument.nachreichung_status === "uebernommen" && " · Werte übernommen"}
                  {dokument.nachreichung_status === "zur_kenntnis" && " · nur abgelegt"}
                </p>
              )}
              <ZuruecknehmenKnopf anlage={anlage} dokument={dokument} />
              {dokument.typ !== "foto" && dokument.extrahierte_daten && (
                <details className="group">
                  <summary className="cursor-pointer text-sm font-semibold text-neutral-700">Rohwerte aus diesem Dokument anzeigen</summary>
                  <div className="mt-3"><FeldListe dokument={dokument} /></div>
                </details>
              )}
            </div>
          ) : (
            <Hinweis art="info">Zu dieser Anlage gibt es noch keine Dokumente.</Hinweis>
          )}
        </Karte>

        {/* ------------------------------------------------------------ Extraktion */}
        <div className="space-y-5">
          {vollmachtDokument && (
            <Karte className="p-5">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500">Vollmacht-Prüfung</h2>
              <VollmachtChecks dokument={vollmachtDokument} />
            </Karte>
          )}
          <Karte className="p-5">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-neutral-500">Extrahierte Daten</h2>
            <AnlagenFormular anlage={anlage} werte={werte} fehler={fehler} geaendert={geaendert} markiert={markiert} nurLesen={nurLesen} beiAenderung={aendern} beiMarkieren={markieren} />
          </Karte>
        </div>
      </div>

      {/* ------------------------------------------------------------ Aktionsleiste */}
      {!nurLesen && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-neutral-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
            <Knopf variante="geist" onClick={verlauf.rueckgaengig} disabled={!verlauf.kannRueckgaengig} title="Rückgängig (Strg+Z)">↶ Rückgängig</Knopf>
            <Knopf variante="geist" onClick={verlauf.wiederholen} disabled={!verlauf.kannWiederholen} title="Wiederholen (Strg+Y)">↷ Wiederholen</Knopf>
            <span className="ml-2 text-xs text-neutral-500">
              {zuSenden.size ? `${zuSenden.size} ungespeicherte Änderung(en)` : "Keine ungespeicherten Änderungen"}
              {Object.keys(clientFehler).length > 0 && <span className="text-ampel-rot"> · {Object.keys(clientFehler).length} Eingabefehler</span>}
            </span>
            <div className="ml-auto flex flex-wrap gap-2">
              <Knopf variante="gefahr" onClick={() => setAblehnenOffen(true)} disabled={beschaeftigt}>Ablehnen (Neuanlauf)</Knopf>
              <Knopf id="knopf-speichern" onClick={() => void beiSpeichern()} laedt={speichern.isPending && !bestaetigen.isPending} disabled={beschaeftigt} title="Speichern (Strg+S)">
                Speichern
              </Knopf>
              <Knopf variante="primaer" onClick={() => setBestaetigenOffen(true)} disabled={beschaeftigt}>Bestätigen (Final)</Knopf>
            </div>
          </div>
        </div>
      )}

      <Modal
        offen={bestaetigenOffen}
        titel="Anlage final bestätigen?"
        beiSchliessen={() => setBestaetigenOffen(false)}
        fuss={
          <>
            <Knopf onClick={() => setBestaetigenOffen(false)}>Abbrechen</Knopf>
            <Knopf variante="primaer" onClick={() => void beiBestaetigen()} laedt={bestaetigen.isPending || speichern.isPending} disabled={fehlendePflicht.length > 0}>
              Jetzt bestätigen
            </Knopf>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p>Alle Werte gelten danach als geprüft, und die Anlage wird schreibgeschützt (Status <strong>final</strong>).</p>
          {zuSenden.size > 0 && <Hinweis art="info">{zuSenden.size} ungespeicherte Änderung(en) werden vorher gespeichert.</Hinweis>}
          {fehlendePflicht.length > 0 && (
            <Hinweis art="fehler">Pflichtfelder fehlen: {fehlendePflicht.map((f) => f.label).join(", ")}</Hinweis>
          )}
          {ungeprueftUnsicher.length > 0 && (
            <Hinweis art="warnung">
              Noch nicht geprüft (Konfidenz ≤ 80 %): <strong>{ungeprueftUnsicher.map((f) => f.label).join(", ")}</strong>. Bitte mit dem Original abgleichen.
            </Hinweis>
          )}
          {!anlage.vollmacht_vorhanden && <Hinweis art="warnung">Es liegt noch keine Vollmacht vor.</Hinweis>}
          {anlage.vollmacht_vorhanden && werte.vollmacht_unterschrieben !== "ja" && (
            <Hinweis art="warnung">Die Vollmacht ist nicht als unterschrieben markiert.</Hinweis>
          )}
        </div>
      </Modal>

      <Modal
        offen={ablehnenOffen}
        titel="Ablehnen und Neuanlauf"
        beiSchliessen={() => setAblehnenOffen(false)}
        fuss={
          <>
            <Knopf onClick={() => setAblehnenOffen(false)}>Abbrechen</Knopf>
            <Knopf variante="gefahr" onClick={beiAblehnen} laedt={ablehnen.isPending} disabled={grund.trim().length < 3}>
              Ablehnen
            </Knopf>
          </>
        }
      >
        <div className="space-y-4 text-sm">
          <p>Die Anlage geht zurück in den Status <strong>Entwurf</strong>. Von dir geprüfte Werte bleiben erhalten.</p>
          <div>
            <label htmlFor="grund" className="mb-1 block text-xs font-semibold">Grund (für das Protokoll)</label>
            <textarea id="grund" rows={3} maxLength={500} className="w-full rounded-lg border border-neutral-300 px-3 py-2" value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. Foto unscharf, falsche Rechnung hochgeladen" />
            {grund.length > 0 && grund.trim().length < 3 && <p className="mt-1 text-xs text-ampel-rot">Mindestens 3 Zeichen</p>}
          </div>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-0.5" checked={neuExtrahieren} onChange={(e) => setNeuExtrahieren(e.target.checked)} />
            <span>
              Alle Dokumente neu mit Claude extrahieren
              <span className="block text-xs text-neutral-500">Ungeprüfte Werte werden neu ermittelt. Das kann pro Dokument einige Sekunden dauern.</span>
            </span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
